/* FLM — photo optimize + crop, no deps (~canvas only).
   optimizeImage(file): downscale to ≤1600px long edge + JPEG q0.85 via canvas
   (re-encoding also strips EXIF) → object URL the cropper consumes.
   PhotoCropper: dark Studio-Chronos overlay — pannable (pointer drag), zoomable
   (slider + two-finger pinch) image inside a fixed crop frame. Main photo gets a
   circular mask + live avatar preview (it becomes the round profile avatar);
   other slots crop at the 4:5 card ratio. Confirm draws the framed region to a
   canvas → JPEG Blob (square ≤1080×1080 / 4:5 ≤1080×1350) for uploadPhoto(). */
import React from 'react';
import { Icon } from './ui';

const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.85;
const MAX_OUT = 1080;

function loadViaImg(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({
      source: img, width: img.naturalWidth, height: img.naturalHeight,
      cleanup: () => URL.revokeObjectURL(url),
    });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load failed')); };
    img.src = url;
  });
}

function loadBitmap(file) {
  if (typeof createImageBitmap === 'function') {
    /* 'from-image' bakes the EXIF orientation in, so the crop math never sees
       rotated coordinates; engines without the option fall back to <img>. */
    return createImageBitmap(file, { imageOrientation: 'from-image' }).then(
      (bmp) => ({ source: bmp, width: bmp.width, height: bmp.height, cleanup: () => bmp.close() }),
      () => loadViaImg(file),
    );
  }
  return loadViaImg(file);
}

/** Phone cameras ship 8–12MB originals — never upload those. Resolves an
    object URL of the downscaled JPEG (EXIF stripped by re-encode). */
export function optimizeImage(file) {
  return loadBitmap(file).then(({ source, width, height, cleanup }) => {
    const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(source, 0, 0, w, h);
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        cleanup();
        if (blob) resolve(URL.createObjectURL(blob));
        else reject(new Error('jpeg encode failed'));
      }, 'image/jpeg', JPEG_QUALITY);
    });
  });
}

/* aspect = frame width / height (1 = square avatar crop, 4/5 = card crop). */
export function PhotoCropper({
  src, round = false, aspect = 1,
  title, hint, zoomLabel, previewLabel, confirmLabel, cancelLabel,
  faNum = (n) => n, onCancel, onConfirm,
}) {
  const FRAME_W = 280;
  const frameH = Math.round(FRAME_W / aspect);
  const imgRef = React.useRef(null);
  const pointers = React.useRef(new Map()); // pointerId → last {x,y} (1 = pan, 2 = pinch)
  const zoomRef = React.useRef(1);
  const [nat, setNat] = React.useState(null); // natural {w,h} once the image decodes
  const [zoom, setZoom] = React.useState(1); // 1..3 over the cover scale
  const [off, setOff] = React.useState({ x: 0, y: 0 }); // frame-px translation from center
  const [busy, setBusy] = React.useState(false);

  const baseScale = nat ? Math.max(FRAME_W / nat.w, frameH / nat.h) : 1; // cover
  const scale = baseScale * zoom;
  const dispW = nat ? nat.w * scale : 0;
  const dispH = nat ? nat.h * scale : 0;
  const ox = (FRAME_W - dispW) / 2 + off.x; // image top-left in frame coords
  const oy = (frameH - dispH) / 2 + off.y;

  /* keep the frame fully covered — no black bars at any zoom/pan */
  const clampOff = React.useCallback((o, z) => {
    if (!nat) return o;
    const s = Math.max(FRAME_W / nat.w, frameH / nat.h) * z;
    const mx = Math.max(0, (nat.w * s - FRAME_W) / 2);
    const my = Math.max(0, (nat.h * s - frameH) / 2);
    return { x: Math.min(mx, Math.max(-mx, o.x)), y: Math.min(my, Math.max(-my, o.y)) };
  }, [nat, frameH]);

  const applyZoom = (z) => {
    const nz = Math.min(3, Math.max(1, z));
    zoomRef.current = nz;
    setZoom(nz);
    setOff((o) => clampOff(o, nz));
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* older webviews */ }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
  };
  const onPointerMove = (e) => {
    const pts = pointers.current;
    const prev = pts.get(e.pointerId);
    if (!prev) return;
    const cur = { x: e.clientX, y: e.clientY };
    if (pts.size === 1) {
      /* one finger → pan */
      setOff((o) => clampOff({ x: o.x + cur.x - prev.x, y: o.y + cur.y - prev.y }, zoomRef.current));
    } else if (pts.size === 2) {
      /* two fingers → pinch zoom (around frame center — cheap and sufficient) */
      const ids = [...pts.keys()];
      const other = pts.get(ids[0] === e.pointerId ? ids[1] : ids[0]);
      const d0 = Math.hypot(prev.x - other.x, prev.y - other.y);
      const d1 = Math.hypot(cur.x - other.x, cur.y - other.y);
      if (d0 > 0) applyZoom(zoomRef.current * (d1 / d0));
    }
    pts.set(e.pointerId, cur);
  };
  const onPointerEnd = (e) => { pointers.current.delete(e.pointerId); };

  /** Framed region → canvas → JPEG Blob (never re-uploads the full photo). */
  const confirm = () => {
    const img = imgRef.current;
    if (!img || !nat || busy) return;
    setBusy(true);
    const sx = -ox / scale;
    const sy = -oy / scale;
    const sw = FRAME_W / scale;
    const sh = frameH / scale;
    const outW = Math.max(320, Math.min(MAX_OUT, Math.round(sw)));
    const outH = Math.round(outW * (frameH / FRAME_W));
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    canvas.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);
    canvas.toBlob((blob) => {
      setBusy(false);
      if (blob) onConfirm(blob);
    }, 'image/jpeg', JPEG_QUALITY);
  };

  const pv = 64 / FRAME_W; // live avatar preview = the exact crop, scaled to 64px

  return (
    <div data-screen-label="Photo cropper" style={{
      position: 'absolute', inset: 0, zIndex: 90, display: 'flex', flexDirection: 'column',
      background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
      animation: 'flmFadeIn 0.25s var(--ease-out-expo)',
    }}>
      <div style={{ padding: '20px 22px 6px', flex: 'none' }}>
        <span className="flm-label">{title}</span>
        <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 7 }}>{hint}</div>
      </div>

      {/* crop frame — pan with a drag, pinch to zoom */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div onPointerDown={onPointerDown} onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd} onPointerCancel={onPointerEnd}
          style={{
            position: 'relative', width: FRAME_W, height: frameH, overflow: 'hidden',
            borderRadius: 16, border: '1px solid var(--border-bright)', background: '#070707',
            touchAction: 'none', cursor: 'grab', boxShadow: 'var(--shadow-card)',
          }}>
          <img ref={imgRef} src={src} alt="" draggable={false}
            onLoad={(e) => setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
            style={{
              position: 'absolute', left: ox, top: oy,
              width: nat ? dispW : 'auto', height: nat ? dispH : 'auto', maxWidth: 'none',
              pointerEvents: 'none', userSelect: 'none', visibility: nat ? 'visible' : 'hidden',
            }} />
          {round ? (
            /* circular avatar mask — the box-shadow dims everything outside the circle */
            <span style={{
              position: 'absolute', inset: 0, borderRadius: '50%', pointerEvents: 'none',
              boxShadow: '0 0 0 600px rgba(0,0,0,0.7)', border: '1px solid rgba(255,212,145,0.6)',
            }}></span>
          ) : null}
        </div>
      </div>

      {/* main photo: live round preview — exactly how the profile avatar renders */}
      {round && nat ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 24px', flex: 'none' }}>
          <span style={{
            position: 'relative', width: 64, height: 64, borderRadius: '50%', overflow: 'hidden',
            flex: 'none', border: '1px solid var(--border-bright)', background: '#070707',
          }}>
            <img src={src} alt="" draggable={false} style={{
              position: 'absolute', left: ox * pv, top: oy * pv,
              width: dispW * pv, height: dispH * pv, maxWidth: 'none', pointerEvents: 'none',
            }} />
          </span>
          <span className="flm-mono" style={{ fontSize: 9, color: 'var(--text-3)' }}>{previewLabel}</span>
        </div>
      ) : null}

      {/* zoom slider */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '16px 24px 0', flex: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="flm-label">{zoomLabel}</span>
          <span className="flm-mono" style={{ fontSize: 9, color: 'var(--accent)' }}>{faNum(Math.round(zoom * 100))}%</span>
        </div>
        <input type="range" className="flm-range" min={100} max={300} value={Math.round(zoom * 100)}
          onChange={(e) => applyZoom(parseInt(e.target.value, 10) / 100)} />
      </div>

      <div style={{ display: 'flex', gap: 10, padding: '16px 20px 22px', flex: 'none' }}>
        <button className="flm-cta is-ghost" onClick={onCancel} style={{ flex: 1 }}>{cancelLabel}</button>
        <button className="flm-cta" onClick={confirm} disabled={!nat || busy} style={{ flex: 1.4 }}>
          <Icon name="check" size={16} strokeWidth={2.5} />
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}
